/**
 * Helper functions for marketplace
*/
import DOMPurify from 'dompurify';
import { getCategoryLogoFromTag } from './categoryMapping';
import { Solution } from '../types';

/**
 * User information interface from authentication/cookies
 */
export interface UserInfo {
    given_name?: string;
    family_name?: string;
    email?: string;
    locale?: string;
}

/**
 * Users data interface from API response
 */
export interface UserData {
    firstname?: string;
    lastname?: string;
    email?: string;
}

/**
 * Get user information from session storage or cookies
 * @returns UserInfo object or null if not found
 */
export const getUserInfo = (): UserInfo | null => {
    try {
        // Try to get from localStorage first
        const localUserInfo = localStorage.getItem('userInfo');
        if (localUserInfo) {
            return JSON.parse(localUserInfo);
        }

        // Try to get from sessionStorage as fallback
        const sessionUserInfo = sessionStorage.getItem('userInfo');
        if (sessionUserInfo) {
            return JSON.parse(sessionUserInfo);
        }

        // Try to get from Okta token storage
        const oktaTokenStorage = localStorage.getItem('okta-token-storage');
        if (oktaTokenStorage) {
            const tokenData = JSON.parse(oktaTokenStorage);
            const idToken = tokenData.idToken;
            if (idToken && idToken.claims) {
                return idToken.claims;
            }
        }

        return null;
    } catch (error) {
        return null;
    }
};

/**
 * Get users data from localStorage 
 * @returns UserData array or null if not found
 */
export const getUsersData = (): UserData[] | null => {
    try {
        const localUsersData = localStorage.getItem('usersData');
        if (localUsersData) {
            return JSON.parse(localUsersData);
        }

        const sessionUsersData = sessionStorage.getItem('usersData');
        if (sessionUsersData) {
            return JSON.parse(sessionUsersData);
        }

        return null;
    } catch (error) {
        return null;
    }
};

/**
 * Save user information to session storage
 * @param userInfo - User information to save
 */
export const saveUserInfo = (userInfo: UserInfo): void => {
    try {
        localStorage.setItem('userInfo', JSON.stringify(userInfo));
    } catch (error) {
        // console.warn('Failed to save userInfo to localStorage:', error);
        // Fallback to sessionStorage if localStorage fails
        try {
            sessionStorage.setItem('userInfo', JSON.stringify(userInfo));
        } catch (fallbackError) {
            // console.warn('Failed to save userInfo to sessionStorage:', fallbackError);
        }
    }
};

/**
 * Save users data to session storage
 * @param usersData - Users data array to save
 */
export const saveUsersData = (usersData: UserData[]): void => {
    try {
        localStorage.setItem('usersData', JSON.stringify(usersData));
    } catch (error) {
        // console.warn('Failed to save usersData to localStorage:', error);
        // Fallback to sessionStorage if localStorage fails
        try {
            sessionStorage.setItem('usersData', JSON.stringify(usersData));
        } catch (fallbackError) {
            // console.warn('Failed to save usersData to sessionStorage:', fallbackError);
        }
    }
};

/**
 * Determine country from locale string
 * @param locale - Locale string (e.g., 'en-US', 'fr-CA', 'en-GB')
 * @returns Country name or empty string if not determinable
 */
export const getCountryFromLocale = (locale?: string): string => {
    if (!locale) return '';
    
    const localeCountryMap: { [key: string]: string } = {
        'US': 'United States',
        'CA': 'Canada',
        'GB': 'United Kingdom',
        'AU': 'Australia',
        'DE': 'Germany',
        'FR': 'France',
        'ES': 'Spain',
        'IT': 'Italy',
        'JP': 'Japan',
        'KR': 'South Korea',
        'CN': 'China',
        'IN': 'India',
        'BR': 'Brazil',
        'MX': 'Mexico',
        'NL': 'Netherlands',
        'SE': 'Sweden',
        'NO': 'Norway',
        'DK': 'Denmark',
        'FI': 'Finland',
        'CH': 'Switzerland',
        'AT': 'Austria',
        'BE': 'Belgium',
        'IE': 'Ireland',
        'NZ': 'New Zealand',
        'SG': 'Singapore',
        'HK': 'Hong Kong',
        'TW': 'Taiwan',
        'ZA': 'South Africa',
        'AR': 'Argentina',
        'CL': 'Chile',
        'CO': 'Colombia',
        'PE': 'Peru',
        'VE': 'Venezuela',
        'RU': 'Russia',
        'PL': 'Poland',
        'CZ': 'Czech Republic',
        'HU': 'Hungary',
        'TR': 'Turkey',
        'IL': 'Israel',
        'AE': 'United Arab Emirates',
        'SA': 'Saudi Arabia',
        'EG': 'Egypt',
        'MA': 'Morocco',
        'TH': 'Thailand',
        'MY': 'Malaysia',
        'ID': 'Indonesia',
        'PH': 'Philippines',
        'VN': 'Vietnam'
    };

    try {
        // Extract country code from locale (e.g., 'en-US' or 'en_US' -> 'US')
        const parts = locale.split(/[-_]/); // Split on both hyphen and underscore
        if (parts.length >= 2) {
            const countryCode = parts[1].toUpperCase();
            const countryName = localeCountryMap[countryCode];
            return countryName || '';
        }
        return '';
    } catch (error) {
        return '';
    }
};

/**
 * Create pre-filled form data from user info and users data
 * @returns Pre-filled form object with user information
 */
export const createPrefilledForm = () => {
    const userInfo = getUserInfo();
    const usersData = getUsersData();
    const currentUserData = usersData && usersData.length > 0 ? usersData[0] : null;

    // Default empty form
    const defaultForm = {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        country: '',
        institution: '',
        jobLevel: '',
        functionalRole: ''
    };

    // If no user info available, return empty form
    if (!userInfo && !currentUserData) {
        return defaultForm;
    }

    // Prefer userInfo for names and email, but fall back to usersData
    const firstName = userInfo?.given_name || currentUserData?.firstname || '';
    const lastName = userInfo?.family_name || currentUserData?.lastname || '';
    const email = userInfo?.email || currentUserData?.email || '';
    const country = getCountryFromLocale(userInfo?.locale);

    return {
        firstName,
        lastName,
        email,
        phone: '', // Not available in either data source
        country,
        institution: '', // Could be derived from domain but not implemented
        jobLevel: '', // Not available in current data sources
        functionalRole: '' // Not available in current data sources
    };
};

/**
 * Get the solution logo or a category-specific logo based on category, or default image if not available/valid.
 * @param image - image url or data encoded string. Only http/s and data protocols are supported. Of the data protocol, only png, jpg, jpeg, svg, svg+xml, and webp are allowed. Base64 encoded is also required for data protocol images.
 * @param category - solution category to determine category-specific logo fallback
 * @returns 
 */
export const solutionLogoOrDefault = (image?: string | null, category?: string) => {
    const defaultImage = '/default-integration-logo.png';
    
    if (!image || image.trim() === '') {
        // If no image provided, try to get category-specific logo based on category
        if (category) {
            const categoryLogo = getCategoryLogoFromTag([category]);
            if (categoryLogo) {
                return categoryLogo;
            }
        }
        return defaultImage;
    }
    
    // Image strings should start with either the data or http/s protocol
    if (/^https?:\/\//.test(image)) {
        return image;
    }
    
    // Check for data URLs and validation
    if (image.startsWith('data:image/')) {
        // Regex to capture image type and base64 data
        const dataUrlMatch = image.match(/^data:image\/(png|jpg|jpeg|svg\+xml|svg|webp);base64,(.+)$/i);
        
        if (!dataUrlMatch) {
            // Invalid data URL, try category-specific logo
            if (category) {
                const categoryLogo = getCategoryLogoFromTag([category]);
                if (categoryLogo) {
                    return categoryLogo;
                }
            }
            return defaultImage;
        }
        
        const base64Data = dataUrlMatch[2];
        
        // Validate base64 data is not empty or too short (catches truncated data)
        if (!base64Data || base64Data.length < 100) {
            // Invalid base64, try category-specific logo
            if (category) {
                const categoryLogo = getCategoryLogoFromTag([category]);
                if (categoryLogo) {
                    return categoryLogo;
                }
            }
            return defaultImage;
        }
        
        // Check for valid base64 characters
        const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
        if (!base64Regex.test(base64Data)) {
            // Invalid base64, try category-specific logo
            if (category) {
                const categoryLogo = getCategoryLogoFromTag([category]);
                if (categoryLogo) {
                    return categoryLogo;
                }
            }
            return defaultImage;
        }
        
        // Try to decode base64 to verify it's valid
        try {
            atob(base64Data);
            return image;
        } catch (error) {
            // Invalid base64, try category-specific logo
            if (category) {
                const categoryLogo = getCategoryLogoFromTag([category]);
                if (categoryLogo) {
                    return categoryLogo;
                }
            }
            return defaultImage;
        }
    }
    
    // Invalid image format, try category-specific logo
    if (category) {
        const categoryLogo = getCategoryLogoFromTag([category]);
        if (categoryLogo) {
            return categoryLogo;
        }
    }
    return defaultImage;
}

/**
 * Sanitize HTML content to prevent XSS attacks.
 * @param htmlContent - HTML content to sanitize
 * @returns Sanitized HTML content
 * 
 * This should be used with React's dangerouslySetInnerHTML attribute, e.g.:
 * <div dangerouslySetInnerHTML={sanitizeContent(htmlContent)}></div>
 */
export const sanitizeContent = (htmlContent: string, stripAllOut:boolean = false) => {
    const config:DOMPurify.Config = {
        ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'a', 'p', 'ul', 'ol', 'li', 'br'],
        ALLOWED_ATTR: ['href', 'target', 'rel'],
    };
    if (stripAllOut) {
        config.ALLOWED_TAGS = [];
    }
    return { __html: DOMPurify.sanitize(htmlContent, config) };
}

/**
 * Decode HTML entities and clean up weird characters from API text
 * @param text - Text that may contain HTML entities or weird characters
 * @returns Cleaned text
 * 
 * Handles:
 * - HTML entities like &lt; &gt; &amp; &#39; &quot;
 * - Unicode replacement characters (�)
 * - Other special character issues from API responses
 */
export const decodeAndCleanText = (text?: string | null): string => {
    if (!text || text.trim() === '') {
        return '';
    }

    try {
        // Create a temporary element to decode HTML entities
        const textarea = document.createElement('textarea');
        textarea.innerHTML = text;
        let decoded = textarea.value;

        // Remove or replace Unicode replacement characters (�) and other problematic chars
        decoded = decoded
            .replace(/\uFFFD/g, '') // Remove Unicode replacement character
            .replace(/\r\n/g, '\n') // Normalize line breaks
            .replace(/\u00A0/g, ' ') // Replace non-breaking spaces with regular spaces
            .trim();

        return decoded;
    } catch (e) {
        return text;
    }
}

/**
 * Convert YouTube URL to embed format
 * @param url - YouTube URL (various formats supported)
 * @returns Embed URL or null if invalid
 * 
 * Supports formats:
 * - https://youtu.be/VIDEO_ID
 * - https://www.youtube.com/watch?v=VIDEO_ID
 * - https://www.youtube.com/embed/VIDEO_ID
 */
export const getYouTubeEmbedUrl = (url?: string | null): string | null => {
    if (!url || url.trim() === '') {
        return null;
    }

    try {
        // Already an embed URL
        if (url.includes('youtube.com/embed/')) {
            return url;
        }

        // Extract video ID from various YouTube URL formats
        let videoId: string | null = null;

        // Format: https://youtu.be/VIDEO_ID or https://youtu.be/VIDEO_ID?si=...
        const shortMatch = url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/);
        if (shortMatch) {
            videoId = shortMatch[1];
        }

        // Format: https://www.youtube.com/watch?v=VIDEO_ID
        const watchMatch = url.match(/youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/);
        if (watchMatch) {
            videoId = watchMatch[1];
        }

        // Format: https://www.youtube.com/v/VIDEO_ID
        const vMatch = url.match(/youtube\.com\/v\/([a-zA-Z0-9_-]+)/);
        if (vMatch) {
            videoId = vMatch[1];
        }

        if (videoId) {
            return `https://www.youtube.com/embed/${videoId}`;
        }

        return null;
    } catch (e) {
        return null;
    }
}

/**
 * Shared helper to check if an entitlement matches a product/solution
 * @internal
 */
const checkEntitlementMatch = (
    entitlements: any[],
    salesforceId?: string,
    fullHierarchy?: string,
    name?: string
): boolean => {
    return entitlements.some((ent) => {
        // Top-level: match salesforceId
        if (salesforceId && ent.salesforceId === salesforceId) {
            return true;
        }
        // Foundation: match ent.name against EITHER solution.fullHierarchy OR solution.name
        if (ent.name) {
            if (fullHierarchy && ent.name === fullHierarchy) {
                return true;
            }
            if (name && ent.name === name) {
                return true;
            }
        }
        return false;
    });
};

/**
 * Check if a product is entitled (top-level by salesforceId OR Foundation by fullHierarchy/name)
 * This is the main function to use for checking product entitlements
 * 
 * @param product - Product with salesforceId and fullHierarchy (or name as fallback)
 * @returns boolean - true if entitled via either top-level or Foundation
 */
export const isProductEntitled = (product: { salesforceId?: string; fullHierarchy?: string; name?: string } | null): boolean => {
    if (!product) return false;

    try {
        const storedEntitlements = localStorage.getItem("entitlementsData");
        if (!storedEntitlements) {
            return false;
        }

        const entitlements = JSON.parse(storedEntitlements);
        if (!Array.isArray(entitlements) || entitlements.length === 0) {
            return false;
        }

        // Pass both fullHierarchy and name for Foundation matching
        return checkEntitlementMatch(entitlements, product.salesforceId, product.fullHierarchy, product.name);
    } catch (err) {
        return false;
    }
}

/**
 * Determine if a solution is entitled for the current user
 * Matches both top-level (by salesforceId) and Foundation (by name/fullHierarchy)
 * 
 * @param solution - Solution object with salesforceId and fullHierarchy
 * @param isLoggedIn - Whether the user is logged in
 * @returns boolean - true if user is logged in and has a matching entitlement
 */
export const isSolutionEntitled = (
    solution: { salesforceId?: string; fullHierarchy?: string; name?: string } | string,
    isLoggedIn?: boolean
): boolean => {
    // Support both old signature (solutionSalesforceId: string) and new signature (solution: object)
    let solutionId: string | undefined;
    let fullHierarchy: string | undefined;
    let loggedIn: boolean;

    if (typeof solution === 'string') {
        // Old signature: (solutionSalesforceId, isLoggedIn)
        solutionId = solution;
        loggedIn = isLoggedIn ?? false;
    } else {
        // New signature: (solution: object, isLoggedIn)
        solutionId = solution?.salesforceId;
        // Use fullHierarchy if available, otherwise fall back to name (which is the solution display name)
        fullHierarchy = solution?.fullHierarchy || solution?.name;
        loggedIn = isLoggedIn ?? false;
    }

    if (!loggedIn) {
        return false;
    }

    try {
        const storedEntitlements = localStorage.getItem("entitlementsData");
        if (!storedEntitlements) {
            return false;
        }

        const userEntitlements = JSON.parse(storedEntitlements);
        if (!Array.isArray(userEntitlements) || userEntitlements.length === 0) {
            return false;
        }

        // Pass both fullHierarchy and name for Foundation matching
        const solutionName = typeof solution === 'string' ? undefined : solution?.name;
        return checkEntitlementMatch(userEntitlements, solutionId, fullHierarchy, solutionName);
    } catch (err) {
        return false;
    }
}

/**
 * @returns Array of entitlements or empty array if not found
 */
export const getEntitlementsData = (): any[] => {
    try {
        const data = localStorage.getItem('entitlementsData');
        if (!data) return [];
        const parsed = JSON.parse(data);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
};

/**
 * Get ERP-specific self-entitlement limits
 * @param erpType - 'Banner' or 'Colleague' from backend
 * @returns Maximum allowed self-entitlements for that ERP
 */
export const getErpLimit = (erpType?: string): number => {
    if (erpType === 'Banner') return 30;
    if (erpType === 'Colleague') return 30;
    return 0;
};

/**
 * Map erpType to the display format used in solutions
 * Backend returns 'Banner' but solutions have 'Ellucian Student (Banner)' after transformation in MainContent
 */
const mapErpTypeToSolutionFormat = (erpType?: string): string | undefined => {
    if (erpType === 'Banner') return 'Ellucian Student (Banner)';
    if (erpType === 'Colleague') return 'Ellucian Student (Colleague)';
    return erpType;
};

/**
 * Count how many Core products are entitled for the user's ERP type
 * Used by MainContent to calculate the accurate widget count
 * @param allSolutions - Array of all solutions
 * @param erpType - User's ERP type (Banner or Colleague) to filter solutions
 * @returns Number of entitled Core products matching user's ERP type
 */
export const countEntitledCoreProducts = (allSolutions?: any[], erpType?: string): number => {
    if (!allSolutions || !Array.isArray(allSolutions)) {
        return 0;
    }
    
    const entitlements = getEntitlementsData();
    if (entitlements.length === 0) {
        return 0;
    }
    
    // Map erpType to solution format (e.g., 'Banner' -> 'Ellucian Student (Banner)')
    const mappedErpType = mapErpTypeToSolutionFormat(erpType);
    
    // Filter only Core products matching user's ERP type
    const coreProducts = allSolutions.filter(
        (sol) => sol.productizedIntegrationType === 'Ellucian Core' && 
                 (sol.salesforceId || sol.fullHierarchy) &&
                 (!mappedErpType || (Array.isArray(sol.integratedEllucianSolutions) && 
                  sol.integratedEllucianSolutions.includes(mappedErpType)))
    );
    
    // Count how many Core products are entitled using shared helper
    return coreProducts.filter((core) => {
        return checkEntitlementMatch(entitlements, core.salesforceId, core.fullHierarchy, core.name);
    }).length;
};

/**
 * Calculate self-entitlement counts based on Core products actually entitled
 * Counts how many Core products (productizedIntegrationType === 'Ellucian Core')
 * exist in the entitlementsData by matching salesforceId or name
 * 
 * @param erpType - From GET /status API (erpType only)
 * @param allSolutions - Optional full solutions array to filter only Core products
 * @returns Object with max limit, current count of entitled Core products, and remaining count
 */
export const calculateSelfEntitlementCounts = (erpType?: string, allSolutions?: any[]) => {
    const max = getErpLimit(erpType);
    
    // Resolve solutions: use passed array, or fall back to localStorage slim catalog
    let resolvedSolutions = allSolutions;
    if (!resolvedSolutions || resolvedSolutions.length === 0) {
        try {
            const cached = localStorage.getItem('marketplace_solutions_catalog');
            if (cached) resolvedSolutions = JSON.parse(cached);
        } catch { /* ignore */ }
    }

    // Only count Core products when solutions are available
    // Pass erpType to filter only matching ERP products
    let count = 0;
    if (resolvedSolutions && resolvedSolutions.length > 0) {
        count = countEntitledCoreProducts(resolvedSolutions, erpType);
    }
    // If solutions not yet loaded, count stays 0 (prevents showing 6/20 temporarily)
    
    const remainingCount = Math.max(0, max - count);
    
    const entitlements = getEntitlementsData();
    const selfEntitledIds = entitlements
        .map((e: any) => e.salesforceId || e.licenseCode)
        .filter(Boolean);
    
    return {
        max,
        count,
        remainingCount,
        selfEntitledIds,
    };
};

/**
 * Calculate search relevance score for a solution.
 * Higher scores indicate better matches.
 * Used to rank search results by relevance.
 * 
 * @param solution - Solution to score
 * @param searchTerms - Array of search terms
 * @returns Relevance score (higher = better match)
 * 
 * Scoring logic:
 * - Name/Tags match (3 points)
 * - BuiltBy/AccountName match (2 points each)
 * - Other metadata match (1 point)
 * - No match = 0 points
 */
export const parseSearchQuery = (query: string): string[] =>
    query
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .split(/[^a-z0-9]+/)
        .filter(Boolean);

export const calculateSearchScore = (
    solution: Solution,
    searchTerms: string[]
): number => {
    if (searchTerms.length === 0) return 1; // Empty query = all match equally

    const fields = [
        { text: solution.name, weight: 3 },
        { text: solution.category, weight: 3 },
        { text: solution.builtBy, weight: 2 },
        { text: solution.accountName, weight: 2 },
        { text: (solution.tags || []).join(' '), weight: 3 },
        { text: (solution.supportedRegions || []).join(' '), weight: 1 },
        { text: (solution.supportedLanguages || []).join(' '), weight: 1 },
        { text: (solution.integratedEllucianSolutions || []).join(' '), weight: 1 },
        {
            text: (solution.dependencies || [])
                .map(d => d.replace(/ellucian\s*/i, '').trim())
                .filter(Boolean)
                .join(' '),
            weight: 1.5,
        },
    ];

    // Pre-parse each field once
    const parsedFields = fields.map(f => ({
        weight: f.weight,
        words: f.text ? parseSearchQuery(f.text) : [] as string[],
    }));

    // For each search term, find the highest-weight field it matches.
    // If a term matches nothing → score 0 (solution filtered out).
    // This allows cross-field queries: "canvas workforce" can match
    // "canvas" in name and "workforce" in tags independently.
    let totalScore = 0;
    for (const term of searchTerms) {
        let bestWeight = 0;
        for (const field of parsedFields) {
            if (field.words.some(word => word.startsWith(term))) {
                bestWeight = Math.max(bestWeight, field.weight);
            }
        }
        if (bestWeight === 0) return 0;
        totalScore += bestWeight;
    }

    return totalScore;
};
